import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertIcon, ArrowLeftIcon, CopyIcon, WithIcon } from "./icons.js";

/**
 * The one page shown when something fails and the user cannot carry on where they are.
 *
 * KEPT IDENTICAL IN BOTH CONSOLES (web/restaurant-console and web/admin-console have no shared UI package);
 * error-page-identical.test.ts fails if the two copies differ.
 *
 * A plain apology, one way out, and the facts a developer needs folded away underneath. The way out is a full
 * reload of "/", not a client-side navigation: whatever went wrong may have left the app's state broken, and
 * "/" is the page that works out where this user belongs (sign in, onboarding, or their restaurant).
 */

declare const __BUILD_VERSION__: string | undefined;
const BUILD = typeof __BUILD_VERSION__ === "string" ? __BUILD_VERSION__ : "dev";

export interface ErrorDetails {
  message: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  /** Path only. The query string is left out: after sign-in it carries the one-time login code. */
  page: string;
  at: string;
  build: string;
  /** Where the failure was caught, e.g. "render" or "sign-in". */
  context?: string;
}

export function errorDetails(
  error: unknown,
  opts: { context?: string; componentStack?: string; now?: Date; location?: { pathname: string } } = {}
): ErrorDetails {
  const e = error instanceof Error ? error : undefined;
  const message = e?.message || (typeof error === "string" ? error : "") || "Unknown error";
  return {
    message,
    ...(e?.name && e.name !== "Error" ? { name: e.name } : {}),
    ...(e?.stack ? { stack: e.stack } : {}),
    ...(opts.componentStack ? { componentStack: opts.componentStack.trim() } : {}),
    page: (opts.location ?? window.location).pathname,
    at: (opts.now ?? new Date()).toISOString(),
    build: BUILD,
    ...(opts.context ? { context: opts.context } : {})
  };
}

/** The details as plain text, in the order a developer reads them. Also what "Copy" puts on the clipboard. */
export function detailsText(d: ErrorDetails): string {
  return [
    `${d.name ? `${d.name}: ` : ""}${d.message}`,
    d.context ? `where: ${d.context}` : "",
    `page: ${d.page}`,
    `time: ${d.at}`,
    `build: ${d.build}`,
    d.stack ? `\n${d.stack}` : "",
    d.componentStack ? `\ncomponent stack:\n${d.componentStack}` : ""
  ]
    .filter(Boolean)
    .join("\n");
}

/** Reload the app at its start. Exported so tests can replace it; jsdom cannot navigate. */
export const backToStart = {
  go: () => window.location.assign("/")
};

export function ErrorPage({ details }: { details: ErrorDetails }) {
  return (
    <div className="error-page" role="alert">
      <div className="error-page-card">
        <AlertIcon size={28} className="error-page-mark" />
        <h1>Something went wrong on our end</h1>
        <p className="error-page-lede">
          Sorry about that. Going back to the start usually sorts it out. If it keeps happening, please let us
          know what you were doing.
        </p>
        <button className="primary" onClick={() => backToStart.go()}>
          <WithIcon icon={<ArrowLeftIcon size={16} />}>Back to start</WithIcon>
        </button>
        <details className="error-page-details">
          <summary>Technical details</summary>
          <pre>{detailsText(details)}</pre>
          <button
            className="ghost"
            onClick={() => void navigator.clipboard?.writeText(detailsText(details)).catch(() => {})}
          >
            <WithIcon icon={<CopyIcon size={15} />}>Copy</WithIcon>
          </button>
        </details>
      </div>
    </div>
  );
}

/**
 * Catches anything that throws while rendering, anywhere below it, and shows the error page instead of a
 * blank screen. React offers this only as a class component.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { details?: ErrorDetails }> {
  state: { details?: ErrorDetails } = {};

  static getDerivedStateFromError(error: unknown): { details: ErrorDetails } {
    return { details: errorDetails(error, { context: "render" }) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error("[console] render failed", error);
    if (info.componentStack) {
      this.setState((s) => (s.details ? { details: { ...s.details, componentStack: info.componentStack!.trim() } } : s));
    }
  }

  render(): ReactNode {
    return this.state.details ? <ErrorPage details={this.state.details} /> : this.props.children;
  }
}
